// ─── API Response Envelope ───────────────────────────────────
export interface ApiListResponse<T> {
  data: T[];
  total: number;
  offset: number;
  limit: number;
}

// ─── Shared types ────────────────────────────────────────────
export interface ApiMerchant {
  id: string;
  name: string;
  /** ISO market code of the row (MIC-3746); lets the portal render Market and local dates without the brands lookup. */
  country?: string;
  logoDarkUrl?: string;
  logoLightUrl?: string;
  inSupportNetwork?: boolean;
}

export interface ApiCampaign {
  id: string;
  name: string;
}

// ─── Row shapes (discriminated union by `type`) ──────────────
export type ApiCampaignType = 'AD_SALES' | 'UPSIZE' | 'DEAL';

// Opportunity statuses (backend)
export type ApiOpportunityStatus =
  | 'DRAFT'
  | 'PENDING_BD'
  | 'PENDING_CHECK'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'TERMINATED'
  | 'EXPIRED'
  | 'PENDING_CONTROLLER'
  | 'COMPLETED';

export type IoValidationStatus = 'PENDING' | 'PASS' | 'FAIL' | 'ERROR';

export type IoValidationCheckStatus = 'PENDING' | 'PASS' | 'FAIL' | 'BLOCKED';
export type IoValidationCheckType = 'SIGNATURE' | 'IDENTITY' | 'PAYMENT';

export interface IoValidationFailureReason {
  code?: string;
  message: string;
}

export interface IoValidationCheckResult {
  type?: IoValidationCheckType;
  status: IoValidationCheckStatus;
  reason?: string;
  message?: string;
  evidence?: Record<string, unknown>;
}

export interface ApiIoValidation {
  id?: string;
  status: IoValidationStatus;
  failureReasons?: Array<IoValidationFailureReason | string>;
  retryable?: boolean;
  checks?: IoValidationCheckResult[] | Partial<Record<'signature' | 'identity' | 'payment', IoValidationCheckResult>>;
}

// Deal asset statuses — mirrors backend ASSET_STATUS enum (constants.ts)
export enum DealAssetStatus {
  DRAFT = 'DRAFT',
  PENDING_MKT = 'PENDING_MKT',
  PENDING_CRM = 'PENDING_CRM',
  PENDING_GD = 'PENDING_GD',
  PENDING_ML = 'PENDING_ML',
  APPROVED = 'APPROVED',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED',
  TERMINATED = 'TERMINATED',
  SOFT_BOOK = 'SOFT_BOOK',
  SOFT_BOOK_INACTIVE = 'SOFT_BOOK_INACTIVE',
  DELETED = 'DELETED',
  NO_ACTION = 'NO_ACTION',
}

/** @deprecated Use DealAssetStatus enum directly */
export type ApiDealAssetStatus = DealAssetStatus;

export interface ApiPackage {
  id: string;
  /** BE field name is `type`; `packageType` is a legacy alias kept for backward compat. */
  type?: 'STANDARD_PACKAGE' | 'A_LA_CARTE' | 'NON_BILLABLE_ASSET';
  packageType?: 'STANDARD_PACKAGE' | 'A_LA_CARTE' | 'NON_BILLABLE_ASSET';
  name: string;
  status: ApiOpportunityStatus;
  startDate: string; // ISO datetime
  endDate: string;
  createdAt: string;
  updatedAt: string;
  /** Active booked assets on the package (MIC-3746); 0 means nothing to show inline. */
  assetCount?: number;
}

export interface ApiBaseRow {
  id: string;
  campaign: ApiCampaign | null;
  merchant: ApiMerchant;
  /** Market the item belongs to. Dates are UTC instants, so this is what resolves them to a
   *  wall-clock time. Optional because the FE ships ahead of the backend that populates it —
   *  every read site falls back to rendering the range without an offset label. */
  country?: string;
  /** Exposure package name for an UPSIZE row; AD_SALES names its packages in `packages`. */
  packageName?: string;
  startDate: string | null;
  endDate: string | null;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  creationSource?: string | null;
}

export interface ApiAdSalesRow extends ApiBaseRow {
  type: 'AD_SALES';
  status: ApiOpportunityStatus;
  packages: ApiPackage[];
  ioValidation?: ApiIoValidation | null;
}

export interface ApiUpsizeRow extends ApiBaseRow {
  type: 'UPSIZE';
  status: ApiOpportunityStatus;
}

export interface ApiDealRow extends ApiBaseRow {
  type: 'DEAL';
  status: ApiDealAssetStatus;
}

export type ApiCampaignListItem = ApiAdSalesRow | ApiUpsizeRow | ApiDealRow;

// ─── Query params for GET /campaign-manager/items ────────────
export interface CampaignListParams {
  q?: string;
  types?: ApiCampaignType[];
  statuses?: string[]; // union across all status enums
  merchantIds?: string[];
  packageTiers?: number[];
  countries?: string[];
  startDate?: string; // ISO date
  endDate?: string;
  sort?: string; // e.g. "createdAt:desc"
  offset?: number;
  limit?: number;
  createdBys?: string[]; // filter by creator email addresses
  creationSources?: string[]; // MERCHANT for self-serve, SHOPBACK for a pitch
}

// ─── AD_SALES create/update ──────────────────────────────────

export interface ApiAddress {
  street: string;
  unit: string;
  postalCode: string;
}

/** Mirrors backend IoAttachmentType enum */
export enum IoAttachmentType {
  SIGNED = 'signed',
  STAMPED = 'stamped',
  OTHER = 'other',
}

/** Payment DTO for AD_SALES opportunity groups — confirmed field mapping 2026-06-05. */
export interface ApiPaymentDto {
  billingInfo: string; // "Direct" or "Agency"
  invoicingUnit: {
    // ── Required IO-generation fields ──────────────────────────────────
    companyName: string;
    address: string; // joined single-line address for IO PDF
    email: string;
    salesforceId?: string;
    // ── Extended merchant-facing fields (MIC-2898) ───────────────────
    // Mirrors InvoicingUnitDto new optional fields (BE payment.dto.ts).
    // Stored in payment.details JSONB — no migration required.
    country?: string;
    companyRegistrationNumber?: string;
    taxRegistrationNumber?: string;
    invoicingAddress?: ApiAddress;
    accountName?: string; // bank account name (internal label)
    accountHolderName?: string;
    billingCurrency?: string;
    billingAddress?: ApiAddress;
    mailingAddress?: ApiAddress;
    billingSameAsInvoicing?: boolean;
    mailingSameAsBilling?: boolean;
  };
  summary: {
    currency: string;
    totalPrice: string;
    totalGstVat: string;
    grandTotal: string;
  };
  scheduleDetail: {
    currency: string;
    totalSalesPrice: string;
    paymentMode: 'FULL_PAYMENT' | 'INSTALLMENT';
    numberOfInstallments?: number;
    gstVatRateType: string;
    gstVatRate?: string;
    authorizerName: string;
    authorizerEmail: string;
    isAuthorizerDifferentCompany?: boolean;
    authorizerCompanyName?: string;
    authorizerContactName?: string;
    authorizerContactEmail?: string;
  };
  invoiceSchedules: Array<{
    paymentRefId: string;
    invoiceDate: string;
    price: string;
    gstVat: string;
    total: string;
  }>;
  insertionOrderReferenceId?: string;
  ioAttachments?: Array<{ fileKey: string; name: string; type: IoAttachmentType }>;
  ioLanguage?: string;
  generatedInvoiceLink?: string;
}

export interface CreateOpportunityGroupRequest {
  type?: 'AD_SALES';
  country: string;
  merchantId: string;
  challengeCode?: string;
  group: {
    campaignId?: string;
    payment?: ApiPaymentDto;
    status?: string;
  };
  packages: Array<{
    /** Present on PATCH to update an existing package row; absent to create a new one. */
    id?: string;
    packageType: 'STANDARD_PACKAGE' | 'A_LA_CARTE' | 'NON_BILLABLE_ASSET';
    startDate: string;
    endDate: string;
    packageTier?: string;
    merchantComment?: string;
    status?: string;
    /** Asset bookings for this package — carries bookingSlots for schedulable assets */
    assets?: AssetBookingAsset[];
    paymentDetails?: { currency: string; salesPrice: string };
  }>;
}

export interface PatchOpportunityGroupRequest {
  group?: {
    status?: ApiOpportunityStatus;
    [key: string]: unknown;
  };
  packages?: Array<{
    id?: string; // present = UPDATE, absent = CREATE
    _delete?: boolean; // true + id = DELETE
    packageType?: string;
    startDate?: string;
    endDate?: string;
    status?: ApiOpportunityStatus;
    [key: string]: unknown;
  }>;
  /** Replace the group's full payment record (BE updateGroupPayment — replaces, not merges) */
  payment?: ApiPaymentDto;
}

// ─── UPSIZE create/update ────────────────────────────────────

/** @deprecated Legacy tier shape with nested `rate` wrapper — superseded by RatsRateTier */
export interface ApiTierDto {
  tierLabel: string;
  marketingLabel: string;
  isNewTier: boolean;
  enabled: boolean;
  rate: {
    commissionType: 'PERCENTAGE' | 'FIXED';
    commissionAmount: string;
    commissionCap?: number | null;
    cashbackType: 'PERCENTAGE' | 'FIXED';
    cashbackAmount: string;
    cashbackCap?: number | null;
  };
}

export interface CreateOpportunityRequest {
  opportunityType: 'UPSIZE' | 'FLASH_SALE' | 'BASELINE';
  status: ApiOpportunityStatus;
  operator: string;
  merchantId: string;
  campaignId?: string;
  country: string;
  startDate: string;
  endDate: string;
  packageTier?: string;
  merchantComment?: string;
  ratsRate: RatsRateWrite;
  upsize?: { networkCheckScreenshots?: string[] };
}

export interface PatchOpportunityRequest {
  status?: ApiOpportunityStatus;
  startDate?: string;
  endDate?: string;
  packageTier?: string;
  merchantComment?: string;
  ratsRate?: RatsRateWrite;
  upsize?: { networkCheckScreenshots?: string[] };
  [key: string]: unknown;
}

// ─── DEAL create/update ──────────────────────────────────────

/** Per-platform content entry for deal assets */
export interface ApiContentDto {
  platform?: string;
  landingPageUrl?: string;
  title?: string;
  description?: string;
  callToAction?: string;
  imageUrl?: string;
  imageAlt?: string;
}

export interface CreateDealAssetRequest {
  opportunityId?: number;
  merchantId?: string;
  country: string;
  startTime: string;
  endTime: string;
  assetStatus?: ApiDealAssetStatus;
  assetName?: string;
  contents?: ApiContentDto[];
  affiliateLink?: string;
}

export interface PatchDealAssetRequest {
  assetStatus?: ApiDealAssetStatus;
  assetName?: string;
  startTime?: string;
  endTime?: string;
  contents?: ApiContentDto[];
  affiliateLink?: string;
  [key: string]: unknown;
}

// ─── Error shapes ────────────────────────────────────────────
export interface ApiError {
  code: string;
  message?: string;
}

export interface FsmTransitionError extends ApiError {
  code: 'INVALID_TRANSITION';
  from: string;
  to: string;
  allowedTargets: string[];
}

export interface OwnershipError extends ApiError {
  code: 'FORBIDDEN_NOT_OWNED';
  id?: string;
}

// ─── Package Catalog ────────────────────────────────────────

export interface ApiPackageCatalogItem {
  id: string;
  country: string;
  key: string;
  order: number;
  name: string;
  type: 'UPSIZE' | 'AD_SALES' | 'ASSET_BOOKING';
  assets: Record<string, number>;
  createSelectable: boolean;
  status: string;
  extra: {
    totalPackageValue?: number;
    salesPrice?: number;
    packageCurrency?: string;
    minCommissionRate?: number;
    typeOptions?: string[];
  } | null;
}

// ─── Package Asset (detail per asset in a package) ─────────

export interface ApiPackageAssetItem {
  id: string;
  name: string;
  group: string;
  type: string;
  status: string;
  isAutomated: boolean;
  packageId: string;
  quantity: number;
  /** timeType returned by V2 package-asset detail (Fix A) */
  timeType?: string;
}

// ─── Asset booking slot range ────────────────────────────────

export interface AssetBookingSlot {
  startTime: string; // ISO datetime
  endTime: string; // ISO datetime
}

// ─── Per-asset shape used when building packages[].assets[] in create/patch requests ─

export interface AssetBookingAsset {
  /** The canonical sales asset ID (references mic_sales_asset) */
  salesAssetId?: string;
  /** Internal ID — same as salesAssetId for newly-built assets */
  id?: string;
  name: string;
  group: string;
  /** Asset type (e.g. "DISPLAY") — required by the backend DTO */
  type?: string;
  isAutomated?: boolean;
  timeType?: string;
  isActive?: boolean;
  quantity: number;
  /** Non-consecutive day picks expressed as merged date ranges */
  bookingSlots?: AssetBookingSlot[];
}

// ─── Brand (account-scoped brand list) ──────────────────────

export interface ApiBrand {
  id: string;
  name: string;
  country: string;
  legacyMerchantId: string | null;
  branchLink: string | null;
  integrationType: string | null;
  networkName: string | null;
  logoDarkUrl: string | null;
  logoLightUrl: string | null;
  inSupportNetwork: boolean;
  affiliateLink: string | null;
}

// ─── Member management ──────────────────────────────────────

export enum WorkspaceRole {
  Admin = 'admin',
  Member = 'member',
}

export enum BrandPreset {
  Admin = 'admin',
  Member = 'member',
  None = 'none', // wire value for revoke; never returned by list/get
}

export interface ApiBrandAccessEntry {
  brandId: string;
  brandName: string;
  preset: BrandPreset.Admin | BrandPreset.Member;
}

export interface ApiMember {
  merchantUserId: string;
  name: string;
  email: string;
  workspaceRole: WorkspaceRole;
  brandAccess: ApiBrandAccessEntry[];
}

export interface ApiListMembersResponse {
  data: ApiMember[];
  meta: { total: number; page: number; limit: number };
}

export interface ApiEditBrandAccessBody {
  brandRoles: Array<{ brandId: string; preset: BrandPreset }>;
}

export interface ApiEditBrandAccessResponse {
  merchantUserId: string;
  brandAccess: ApiBrandAccessEntry[];
}

// ─── Wizard Init Info (reference data) ──────────────────────

export interface InitInfoBrand {
  id: string;
  name: string;
  category: string;
}

export interface InitInfoMerchant {
  id: string;
  merchantId: string;
  name: string;
  country: string;
  countryName: string;
  brands: InitInfoBrand[];
  campaigns: Array<{ id: string; name: string; start: string; end: string }>;
}

export const AssetGroup = {
  AUTO: 'AUTO',
  CRM: 'CRM',
  EDM: 'EDM',
  MANUAL: 'MANUAL',
  DEAL: 'DEAL',
} as const;
export type AssetGroup = (typeof AssetGroup)[keyof typeof AssetGroup];

export interface InitInfoExposurePackage {
  id: string;
  key?: string;
  country?: string;
  name: string;
  tier: string;
  minCommissionRate: number;
  color: string;
  summary?: string;
  assets: Array<{ id: string; name: string; group: AssetGroup }>;
}

export interface InitInfoBaselineTier {
  id: string;
  category: string;
  baseline: number;
  unit: '$' | '%';
}

export interface InitInfoCountryConfig {
  timezone: string;
  timezoneAbbr: string;
  currencies: string[];
}

export interface InitInfoAdPackageAsset {
  id: string;
  name: string;
  group: 'AUTO' | 'MANUAL' | 'CRM' | 'EDM';
  duration?: string;
  qty?: number;
  mode?: 'Auto' | 'Manual';
}

export interface InitInfoAdPackageSurface {
  label: string;
  assets: InitInfoAdPackageAsset[];
}

export interface InitInfoAdPackage {
  id: string;
  name: string;
  description: string;
  priceSGD: number;
  priceAUD?: number;
  valueSGD?: number;
  valueAUD?: number;
  durationDays: number;
  durationLabel: string;
  surfaces: InitInfoAdPackageSurface[];
  slotsAvailable: number;
  soldOut?: boolean;
  topBadge?: string;
}

export interface WizardInitInfo {
  merchant: InitInfoMerchant | null;
  exposurePackages: InitInfoExposurePackage[];
  baselineTiers: InitInfoBaselineTier[];
  adPackages: InitInfoAdPackage[];
  countryConfig: Record<string, InitInfoCountryConfig>;
  blockedDatesByPackage: Record<string, string[]>;
  gstByCountry: Record<string, number>;
}

// ─── Campaign Selection (linked campaign picker) ────────────

export interface CampaignSelectionItem {
  id: string;
  name: string;
  start: string;
  end: string;
}

export interface CampaignSelectionParams {
  countries?: string;
  q?: string;
  page?: number;
  limit?: number;
}

// ─── MOOP campaign picker API types ─────────────────────────

export interface ApiMoopCampaignItem {
  id: string;
  name: string;
  country: string;
  type: string | null;
  status: string;
  start: string | null;
  end: string | null;
  teaserStart: string | null;
  teaserEnd: string | null;
}

export interface ApiMoopCampaignsResponse {
  data: ApiMoopCampaignItem[];
  currentPage: number;
  totalItems: number;
  totalPages: number;
}

// ─── File Upload ────────────────────────────────────────────

export interface FileUploadResult {
  uid: string;
  name: string;
  status: 'done' | 'error';
  url?: string;
  fileKey?: string;
}

// ─── Save Draft ─────────────────────────────────────────────

export type DraftCampaignType = 'UPSIZE' | 'AD_SALES' | 'STANDALONE_OFFER';

// ─── Duplicate Campaign ─────────────────────────────────────

export interface DuplicateCampaignData {
  type: 'UPSIZE' | 'AD_SALES' | 'STANDALONE_OFFER';
  sourceId: string;
  formData: Record<string, unknown>;
  /** Country from source campaign — used as fallback when brand isn't in initial brands list */
  country?: string;
}

// ─── Baseline V2 ────────────────────────────────────────────

export enum RateType {
  PERCENTAGE = 'PERCENTAGE',
  FIXED = 'FIXED',
}

/** Mirrors backend AssetTimeType enum (opportunity.dto.ts) */
export enum AssetTimeType {
  START_ONLY = 'startOnly',
  START_END = 'startEnd',
}

export interface RatsRateTier {
  /** Present when targeting an existing stored tier on PATCH */
  id?: string;
  tierLabel: string;
  marketingLabel: string;
  isNewTier: boolean;
  enabled: boolean;
  commissionType: RateType;
  commissionRate: number;
  commissionCap: number | null;
  cashbackType: RateType;
  cashbackRate: number;
  cashbackCap: number | null;
}

export interface RatsRate {
  startTime: string;
  isSetOrderCap: boolean;
  orderCap: number | null;
  orderCommissionCap: number | null;
  isSetFixedMargin: boolean;
  targetMarginRate: number;
  tiers: RatsRateTier[];
}

/** Write-side ratsRate shape — same as RatsRate minus the output-only `startTime` field */
export type RatsRateWrite = Omit<RatsRate, 'startTime'>;

export interface BaselineV2Response {
  merchant: { id: string; networkName: string | null };
  ratsRate: RatsRate | null;
  cashbackCoreRate: unknown | null;
}

export interface ApiInvoicingUnit {
  name: string;
  salesforceId: string;
  country: string;
  companyRegistrationNumber?: string;
  gstNumber?: string;
  billingAddress?: { street?: string; unit?: string; postalCode?: string };
  mailingAddress?: { street?: string; unit?: string; postalCode?: string };
  billingToMailing?: boolean;
  billingAddressString?: string;
  mailingAddressString?: string;
  accountHolderName?: string;
  billingEmail?: string;
  currency?: string;
}
